<script setup lang="ts">
import TripView from './TripView.vue';
import StationWaiting from './StationWaiting.vue';
import FinalDestination from './FinalDestination.vue';
import type TripSegment from '@itinerary/trip/TripSegment';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';

const { segment, compact, toggleable, priceClass } = defineProps<IProps>();
interface IProps {
    segment: TripSegment;
    toggleable?: boolean;
    compact?: boolean;
    priceClass?: TripPriceClass;
}
const isOpen = ref(true);
const title = computed(
    () =>
        `${segment.firstTrip.originCityName} به ${segment.lastTrip.destinationCityName}`
);
const dateString = computed(() => {
    const departure = dayConfig
        .js(segment.firstTrip?.departureDateTime)
        .format(formatter({ format: 'long', includeWeekday: true }));
    const arrival = dayConfig
        .js(segment.lastTrip?.arrivalDateTime)
        .format(formatter({ format: 'long', includeWeekday: true }));
    if (departure !== arrival) {
        return `${departure} ← ${arrival}`;
    }
    return departure;
});
const segmentOptions = computed(() =>
    compact ? segment.compactOptions : segment.options
);
const options = computed(() => [
    ...segmentOptions.value,
    ...(priceClass?.priceOptions ?? []),
]);
const trips = computed(() => segment.trips);
const finalDestination = computed(() => {
    if (
        segment.lastTrip?.finalDestination &&
        !segment.lastTrip?.destination.includes(
            segment.lastTrip?.finalDestination
        )
    ) {
        return segment.lastTrip.finalDestination;
    }
    return null;
});
const toggle = () => {
    if (!toggleable) {
        return;
    }
    isOpen.value = !isOpen.value;
};
</script>

<template>
    <div class="segment-info-container">
        <header class="details-header" @click="toggle">
            <mr-dropdown
                v-if="toggleable"
                v-model="isOpen"
                class="segment-button"
                variant="transparent"
                :title
                only-button
            />
            <div class="segment-date">
                {{ dateString }}
            </div>
            <template
                v-for="(option, index) in options"
                :key="`divider-${index}`"
            >
                <mr-divider
                    class="mr-divider"
                    vertical
                    size="20px"
                    color="var(--blue-200)"
                    :thickness="1"
                />
                <div class="option-container">
                    <mr-icon
                        v-if="option.icon"
                        :icon="option.icon"
                        class="option-icon"
                    />
                    <div
                        class="title"
                        :class="{
                            'loading-skeleton with-text': option.loading,
                        }"
                    >
                        {{ option.title }}
                    </div>
                </div>
            </template>
        </header>
        <transition>
            <div v-show="isOpen" class="content-container">
                <template v-for="(trip, tripIndex) in trips" :key="tripIndex">
                    <StationWaiting
                        v-if="tripIndex > 0"
                        :arrival-trip="trips[tripIndex - 1]"
                        :departure-trip="trip"
                    />
                    <TripView
                        :trip
                        :is-first="tripIndex === 0"
                        :compact
                        :has-final-destination="!!finalDestination"
                    />
                </template>
                <FinalDestination
                    v-if="finalDestination"
                    key="final-destination"
                    :destination="finalDestination"
                />
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>
@include v-transition-scale-y();
.segment-info-container {
    margin-bottom: 16px;
    &:last-child {
        margin-bottom: 0;
    }
}

.details-header {
    @include tg-m($color: usePalette($p-blue, 500));
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 8px 12px;
    background: usePalette($p-blue, 100);
    border-radius: 8px;
    min-height: 44px;
    .mr-divider {
        margin-inline: 12px;
    }
    .option-container {
        display: flex;
        align-items: center;
        .option-icon {
            margin-left: 4px;
        }
    }
}

.content-container {
    margin-top: 16px;
}
</style>
